import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ComparativeAnalysis = () => {
  const [selectedItems, setSelectedItems] = useState(['rolex_submariner', 'omega_speedmaster']);
  const [comparisonType, setComparisonType] = useState('price');

  const availableItems = [
    {
      id: 'rolex_submariner',
      name: 'Rolex Submariner 5513',
      category: 'Vintage Watches',
      currentPrice: 15500,
      image: 'https://images.unsplash.com/photo-1523170335258-f5c6c6bd6eaf?w=100&h=100&fit=crop&crop=center'
    },
    {
      id: 'omega_speedmaster',
      name: 'Omega Speedmaster Professional',
      category: 'Vintage Watches',
      currentPrice: 4200,
      image: 'https://images.unsplash.com/photo-1594534475808-b18fc33b045e?w=100&h=100&fit=crop&crop=center'
    },
    {
      id: 'patek_philippe',
      name: 'Patek Philippe Calatrava',
      category: 'Vintage Watches',
      currentPrice: 28500,
      image: 'https://images.unsplash.com/photo-1548181207-7f5ac2b60904?w=100&h=100&fit=crop&crop=center'
    },
    {
      id: 'jordan_rookie',
      name: '1986 Fleer Michael Jordan',
      category: 'Sports Cards',
      currentPrice: 45000,
      image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=100&h=100&fit=crop&crop=center'
    }
  ];

  const priceComparisonData = [
    { period: 'Jan 2024', rolex_submariner: 14200, omega_speedmaster: 3800, patek_philippe: 26500, jordan_rookie: 38000 },
    { period: 'Feb 2024', rolex_submariner: 14500, omega_speedmaster: 3900, patek_philippe: 27000, jordan_rookie: 40000 },
    { period: 'Mar 2024', rolex_submariner: 14800, omega_speedmaster: 4000, patek_philippe: 27500, jordan_rookie: 42000 },
    { period: 'Apr 2024', rolex_submariner: 15000, omega_speedmaster: 4100, patek_philippe: 28000, jordan_rookie: 43500 },
    { period: 'May 2024', rolex_submariner: 15200, omega_speedmaster: 4150, patek_philippe: 28200, jordan_rookie: 44000 },
    { period: 'Jun 2024', rolex_submariner: 15300, omega_speedmaster: 4180, patek_philippe: 28300, jordan_rookie: 44500 },
    { period: 'Jul 2024', rolex_submariner: 15400, omega_speedmaster: 4200, patek_philippe: 28400, jordan_rookie: 45000 },
    { period: 'Aug 2024', rolex_submariner: 15450, omega_speedmaster: 4200, patek_philippe: 28450, jordan_rookie: 45200 },
    { period: 'Sep 2024', rolex_submariner: 15500, omega_speedmaster: 4200, patek_philippe: 28500, jordan_rookie: 45000 }
  ];

  const performanceMetrics = [
    {
      item: 'rolex_submariner',
      metrics: [
        { subject: 'Price Growth', A: 85 },
        { subject: 'Market Demand', A: 92 },
        { subject: 'Liquidity', A: 88 },
        { subject: 'Rarity Score', A: 75 },
        { subject: 'Condition Premium', A: 80 },
        { subject: 'Investment Grade', A: 90 }
      ]
    },
    {
      item: 'omega_speedmaster',
      metrics: [
        { subject: 'Price Growth', A: 65 },
        { subject: 'Market Demand', A: 78 },
        { subject: 'Liquidity', A: 85 },
        { subject: 'Rarity Score', A: 60 },
        { subject: 'Condition Premium', A: 70 },
        { subject: 'Investment Grade', A: 75 }
      ]
    }
  ];

  const comparisonStats = [
    {
      metric: 'Current Price',
      rolex_submariner: '$15,500',
      omega_speedmaster: '$4,200',
      patek_philippe: '$28,500',
      jordan_rookie: '$45,000'
    },
    {
      metric: '6M Growth',
      rolex_submariner: '+8.5%',
      omega_speedmaster: '+5.3%',
      patek_philippe: '+7.2%',
      jordan_rookie: '+18.4%'
    },
    {
      metric: 'YTD Growth',
      rolex_submariner: '+9.2%',
      omega_speedmaster: '+10.5%',
      patek_philippe: '+7.5%',
      jordan_rookie: '+18.4%'
    },
    {
      metric: 'Volatility',
      rolex_submariner: 'Low',
      omega_speedmaster: 'Low',
      patek_philippe: 'Medium',
      jordan_rookie: 'High'
    },
    {
      metric: 'Liquidity',
      rolex_submariner: 'High',
      omega_speedmaster: 'High',
      patek_philippe: 'Medium',
      jordan_rookie: 'Medium'
    }
  ];

  const getItemColor = (itemId) => {
    const colors = {
      rolex_submariner: '#D4AF37',
      omega_speedmaster: '#3B82F6',
      patek_philippe: '#8B5CF6',
      jordan_rookie: '#10B981'
    };
    return colors?.[itemId] || '#A0A3B1';
  };

  const toggleItemSelection = (itemId) => {
    if (selectedItems?.includes(itemId)) {
      if (selectedItems?.length > 1) {
        setSelectedItems(selectedItems?.filter(id => id !== itemId));
      }
    } else {
      if (selectedItems?.length < 4) {
        setSelectedItems([...selectedItems, itemId]);
      }
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-popover-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center space-x-2">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry?.color }}
              ></div>
              <span className="text-sm text-popover-foreground">
                {availableItems?.find(item => item?.id === entry?.dataKey)?.name}: ${entry?.value?.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        <div>
          <h2 className="text-xl font-bold text-foreground">Comparative Analysis</h2>
          <p className="text-sm text-muted-foreground">Compare performance across different collectibles</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <div className="flex items-center bg-muted/30 rounded-lg p-1">
            <button
              onClick={() => setComparisonType('price')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                comparisonType === 'price' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Price Trends
            </button>
            <button
              onClick={() => setComparisonType('performance')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                comparisonType === 'performance' 
                  ? 'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Performance
            </button>
          </div>

          <Button
            variant="outline"
            iconName="Download"
            iconPosition="left"
            size="sm"
          >
            Export
          </Button>
        </div>
      </div>
      {/* Item Selection */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-card-foreground mb-4">Select Items to Compare</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {availableItems?.map((item) => (
            <div
              key={item?.id}
              onClick={() => toggleItemSelection(item?.id)}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 premium-hover ${
                selectedItems?.includes(item?.id)
                  ? 'border-primary bg-primary/5' :'border-border bg-muted/20 hover:border-muted'
              }`}
            >
              <div className="flex items-center space-x-3">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: getItemColor(item?.id) }}
                ></div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-card-foreground truncate">{item?.name}</h4>
                  <p className="text-sm text-muted-foreground">{item?.category}</p>
                  <p className="text-sm font-semibold text-card-foreground">
                    ${item?.currentPrice?.toLocaleString()}
                  </p>
                </div>
                {selectedItems?.includes(item?.id) && (
                  <Icon name="Check" size={20} className="text-primary" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Price Comparison Chart */}
      {comparisonType === 'price' && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-card-foreground">Price Trends Comparison</h3>
            <div className="flex items-center space-x-4">
              {selectedItems?.map((itemId) => {
                const item = availableItems?.find(i => i?.id === itemId);
                return (
                  <div key={itemId} className="flex items-center space-x-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: getItemColor(itemId) }}
                    ></div>
                    <span className="text-sm text-card-foreground">{item?.name}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={priceComparisonData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis 
                  dataKey="period" 
                  stroke="#A0A3B1"
                  fontSize={12}
                />
                <YAxis 
                  stroke="#A0A3B1"
                  fontSize={12}
                  tickFormatter={(value) => `$${(value / 1000)?.toFixed(0)}k`}
                />
                <Tooltip content={<CustomTooltip />} />
                {selectedItems?.map((itemId) => (
                  <Bar
                    key={itemId}
                    dataKey={itemId}
                    fill={getItemColor(itemId)}
                    radius={[2, 2, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      {/* Performance Radar Chart */}
      {comparisonType === 'performance' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {selectedItems?.slice(0, 2)?.map((itemId) => {
            const item = availableItems?.find(i => i?.id === itemId);
            const metrics = performanceMetrics?.find(m => m?.item === itemId)?.metrics || [];
            
            return (
              <div key={itemId} className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div 
                    className="w-4 h-4 rounded-full" 
                    style={{ backgroundColor: getItemColor(itemId) }}
                  ></div>
                  <h3 className="text-lg font-semibold text-card-foreground">{item?.name}</h3>
                </div>

                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={metrics}>
                      <PolarGrid stroke="rgba(255,255,255,0.1)" />
                      <PolarAngleAxis 
                        dataKey="subject" 
                        tick={{ fill: '#A0A3B1', fontSize: 12 }}
                      />
                      <PolarRadiusAxis 
                        angle={90} 
                        domain={[0, 100]} 
                        tick={{ fill: '#A0A3B1', fontSize: 10 }}
                      />
                      <Radar
                        name={item?.name}
                        dataKey="A"
                        stroke={getItemColor(itemId)}
                        fill={getItemColor(itemId)}
                        fillOpacity={0.1}
                        strokeWidth={2}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* Comparison Table */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-card-foreground mb-4">Detailed Comparison</h3>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Metric</th>
                {selectedItems?.map((itemId) => {
                  const item = availableItems?.find(i => i?.id === itemId);
                  return (
                    <th key={itemId} className="text-left py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: getItemColor(itemId) }}
                        ></div>
                        <span className="text-sm font-medium text-card-foreground">
                          {item?.name}
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {comparisonStats?.map((stat, index) => (
                <tr key={index} className="border-b border-border/50">
                  <td className="py-3 px-4 text-sm font-medium text-card-foreground">
                    {stat?.metric}
                  </td>
                  {selectedItems?.map((itemId) => (
                    <td key={itemId} className="py-3 px-4 text-sm text-card-foreground">
                      {stat?.[itemId] || '-'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ComparativeAnalysis;